import React, { useLayoutEffect, useRef, useState } from "react";
import QRCode from "react-qr-code";
import { Button, Modal, ModalBody, ModalHeader } from "reactstrap";
import { careNotesLabel, careSectionLabels, careTitle } from "./careCopy";
import type { CareLang, ResolvedCare } from "./careTypes";
import { careLines, certificateUrl, pickText } from "./careValues";

type CardLang = CareLang | "both";

/** Styles for the A5 care card, used by the on-screen preview and the print frame alike. */
export const CARE_CARD_CSS = `
.hkp-care-card { box-sizing: border-box; width: 484px; padding: 0; color: #1a1a1a; background: #fff;
  font: 11px/1.4 -apple-system, "Segoe UI", Roboto, "Noto Sans", "Noto Sans Devanagari", Arial, sans-serif; }
.hkp-care-card * { box-sizing: border-box; line-height: 1.4; letter-spacing: normal; }
.hkp-care-card h1, .hkp-care-card h2, .hkp-care-card h3, .hkp-care-card p, .hkp-care-card ul { padding-top: 0; padding-bottom: 0; }
.hkp-care-card__head { text-align: center; padding-bottom: 8px; margin-bottom: 8px; border-bottom: 1px solid #d8cfc0; }
.hkp-care-card__eyebrow { letter-spacing: .14em; text-transform: uppercase; font-size: 8.5px; color: #8c673e; font-weight: 600; }
.hkp-care-card__head h1 { margin: 4px 0 0; font-size: 19px; font-weight: 600; color: #262d22; }
.hkp-care-card__head h2 { margin: 2px 0 0; font-size: 12px; font-weight: 500; color: #404b3b; }
.hkp-care-card__piece { margin: 4px 0 0; font-size: 10.5px; color: #555; }
.hkp-care-card__grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px 14px; }
.hkp-care-card__sec { break-inside: avoid; }
.hkp-care-card__sec h3 { margin: 0 0 2px; font-size: 12px; font-weight: 600; color: #404b3b; }
.hkp-care-card__sec h3 small { font-weight: 400; color: #8c673e; font-size: 10px; margin-left: 4px; }
.hkp-care-card__sec ul { margin: 0; padding-left: 13px; }
.hkp-care-card__sec li { margin: 0 0 1px; }
.hkp-care-card__sec .hi { color: #404b3b; }
.hkp-care-card__notes { grid-column: 1 / -1; padding: 5px 8px; background: #f7f3ec; border-left: 3px solid #c59c6c; }
.hkp-care-card__foot { display: flex; align-items: center; justify-content: space-between; gap: 12px;
  margin-top: 10px; padding-top: 8px; border-top: 1px solid #d8cfc0; }
.hkp-care-card__foot p { margin: 0; font-size: 10.5px; }
.hkp-care-card__foot strong { display: block; font-size: 11.5px; color: #262d22; }
.hkp-care-card__qr { flex: none; text-align: center; font-size: 8.5px; color: #555; }
.hkp-care-card__qr svg { width: 72px; height: 72px; display: block; margin: 0 auto 2px; }
`;

type SheetProps = { care: ResolvedCare; lang: CareLang; productName?: string; studioName?: string; productUniqueId: string };

/** One side of the card, in one language. Kept plain so it reads well in black and white too. */
export const CareCardSheet = ({ care, lang, productName, studioName, productUniqueId }: SheetProps) => {
  const url = certificateUrl(productUniqueId);
  const notes = pickText(care.additionalNotes, lang);
  return (
    <div className="hkp-care-card" lang={lang}>
      <div className="hkp-care-card__head">
        <div className="hkp-care-card__eyebrow">{studioName || "Handwoven with care"}</div>
        <h1>{careTitle[lang]}</h1>
        {productName && <p className="hkp-care-card__piece">{productName}</p>}
      </div>
      <div className="hkp-care-card__grid">
        {notes && (
          <div className="hkp-care-card__notes">
            <strong>{careNotesLabel[lang]}</strong>
            <div>{careLines(notes).join(" ")}</div>
          </div>
        )}
        {care.sections.map((section) => {
          const lines = careLines(pickText(section, lang));
          if (!lines.length) return null;
          return (
            <div className="hkp-care-card__sec" key={section.key}>
              <h3>{careSectionLabels[section.key][lang]}</h3>
              <ul>
                {lines.map((line, i) => (
                  <li key={i}>{line}</li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
      <div className="hkp-care-card__foot">
        <p>
          <strong>{lang === "hi" ? "प्रामाणिकता प्रमाणपत्र" : "Certificate of authenticity"}</strong>
          {lang === "hi"
            ? "कोड स्कैन करें — इस कृति की प्रामाणिकता, इसके बनने की कहानी और यही देखभाल निर्देश कभी भी देखें।"
            : "Scan the code to verify this piece, see its making story and read this care guide any time."}
        </p>
        {url && (
          <div className="hkp-care-card__qr">
            <QRCode value={url} size={72} />
            HastKala Pramanak
          </div>
        )}
      </div>
    </div>
  );
};

// A5 portrait with 10 mm margins leaves 128 × 190 mm: 484 × 718 px at 96 dpi (fitted to 97% for printers).
const PAGE_H = Math.floor(718 * 0.97);
/** Shrink a side that runs a little long so it stays on one page; never below 75% so text stays readable. */
export const cardScale = (height: number): number =>
  height <= PAGE_H ? 1 : Math.max(0.75, Math.floor((PAGE_H / height) * 1000) / 1000);

/** Print just the card from a hidden frame, so the app around it never ends up on paper. A5 portrait, one side per page. */
const printCard = (pages: { html: string; scale: number }[], title: string) => {
  const frame = document.createElement("iframe");
  frame.setAttribute("aria-hidden", "true");
  Object.assign(frame.style, { position: "fixed", right: "0", bottom: "0", width: "0", height: "0", border: "0" });
  document.body.appendChild(frame);
  const doc = frame.contentWindow?.document;
  if (!doc || !frame.contentWindow) {
    frame.remove();
    return;
  }
  const body = pages
    .map((p, i) => `<div class="hkp-care-card-page" style="zoom:${p.scale};${i < pages.length - 1 ? "break-after:page;" : ""}">${p.html}</div>`)
    .join("");
  doc.open();
  doc.write(`<!doctype html><html><head><meta charset="utf-8"><title>${title.replace(/[<>]/g, "")}</title>
<style>@page { size: A5 portrait; margin: 10mm; } html, body { margin: 0; background: #fff; }
${CARE_CARD_CSS}</style></head><body>${body}</body></html>`);
  doc.close();
  const win = frame.contentWindow;
  window.setTimeout(() => {
    win.focus();
    win.print();
    window.setTimeout(() => frame.remove(), 1000);
  }, 250);
};

type Props = Omit<SheetProps, "lang"> & { open: boolean; onClose: () => void };

/** Preview the A5 care card for the box, pick its language, then print or save it as PDF. Both languages = two sides. */
const CareCardPrint = ({ open, onClose, ...sheet }: Props) => {
  const [lang, setLang] = useState<CardLang>("both");
  const langs: CareLang[] = lang === "both" ? ["en", "hi"] : [lang];
  const refs = useRef<(HTMLDivElement | null)[]>([]);
  const [scales, setScales] = useState<number[]>([1, 1]);

  useLayoutEffect(() => {
    if (!open) return;
    const id = window.setTimeout(() => {
      setScales(langs.map((_, i) => cardScale((refs.current[i]?.firstElementChild as HTMLElement | null)?.offsetHeight || 0)));
    }, 50);
    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, lang]);

  const print = () =>
    printCard(
      langs.map((_, i) => ({ html: refs.current[i]?.innerHTML || "", scale: scales[i] || 1 })),
      `Care card ${sheet.productName || ""}`
    );

  return (
    <Modal isOpen={open} toggle={onClose} size="lg" centered>
      <ModalHeader toggle={onClose}>Care card</ModalHeader>
      <ModalBody>
        <style>{CARE_CARD_CSS}</style>
        <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
          <label htmlFor="hkp-care-card-lang" className="mb-0">
            Language
          </label>
          <select id="hkp-care-card-lang" className="form-select w-auto" value={lang} onChange={(e) => setLang(e.target.value as CardLang)}>
            <option value="both">English + हिंदी (two sides)</option>
            <option value="en">English</option>
            <option value="hi">हिंदी</option>
          </select>
          <span className="text-muted small">A5 portrait. In the print dialog choose “Save as PDF” to keep a file.</span>
        </div>
        <div className="d-flex flex-wrap justify-content-center gap-3" style={{ overflowX: "auto", background: "#f1efe9", padding: 16, borderRadius: 8 }}>
          {langs.map((l, i) => (
            <figure key={l} className="mb-0">
              {lang === "both" && (
                <figcaption className="small text-muted mb-1">{i === 0 ? "Front · English" : "Back · हिंदी"}</figcaption>
              )}
              <div style={{ background: "#fff", padding: 18, boxShadow: "0 1px 4px rgba(0,0,0,.12)" }}>
                <div
                  ref={(el) => {
                    refs.current[i] = el;
                  }}
                  style={{ zoom: scales[i] || 1 } as React.CSSProperties}
                >
                  <CareCardSheet {...sheet} lang={l} />
                </div>
              </div>
            </figure>
          ))}
        </div>
        <div className="d-flex justify-content-end gap-2 mt-3">
          <Button className="btn-hastkala-outline" onClick={onClose}>
            Close
          </Button>
          <Button color="primary" onClick={print}>
            Print
          </Button>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default CareCardPrint;
